import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, ReplaySubject, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { delay, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';

import { DataService } from '../../shared/services/data.service';
import { ConfigurationService } from '../../shared/services/configuration.service';
import {
  IProject,
  IAssociateFileUpload,
  IUpdateDeviceWithConflict,
  ICompanyAdminsOfProjects
} from '../../shared/models/project.model';
import { environment } from '../../../environments/environment';
import {
  IEnclosure, IEnclosureInfo,
  IEnclosureOccupancy,
  IEnclosureProjectOverview,
  IEnclosureProperties
} from '../../shared/models/enclosure.model';
import { IProduct } from '../../shared/models/product.model';
import {
  IGridPositionIndex,
  IPlacedDevice,
  IPosition
} from '../../enclosure/models/grid.model';
import { IDevicesOnGrid } from '../../enclosure/models/device-data.model';
import { BomService } from '../../enclosure/services/bom.service';
import { IPragmaDTO, IPrismaXSDTO, ISaveEnclosure } from '../../enclosure/models/save-enclosure.model';
import {
  filterOutDevicesThatShouldNotBePlacedInBOM
} from '../../prisma-xs/helpers/bom-mapper.helper';
import { IDevice } from '../../shared/models/device.model';
import { ICopyEnclosure } from '../../project-layer/models/copy-enclosure.model';
import { updateEnclosureProperty } from '../../enclosure/helpers/enclosure.helper';
import { updateDeviceFactory, updateMultipleDevicesFactory } from '../../enclosure/helpers/product.helper';
import { BreakingCapacityType } from '../../enclosure/models/enclosure-type.model';
import { IWizardConfigurationDevicesDto } from '../../enclosure/models/wizard-device.model';
import { CanEditProjectPipe } from '../../ui/pipes/can-edit-project.pipe';

@Injectable({
  providedIn: 'root'
})
export class ProjectManagementService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private urls: Record<string, any>;

  private currentProject$ = new ReplaySubject<IProject>(1);
  private currentEnclosure$ = new ReplaySubject<IEnclosure>(1);
  private currentEnclosureDeviceCount$ = new BehaviorSubject<number>(0);
  private currentProjectEditable$ = new BehaviorSubject<boolean>(true);

  constructor(
    private readonly dataService: DataService,
    private readonly bomService: BomService,
    private readonly httpClient: HttpClient,
    private readonly configurationService: ConfigurationService) {

    this.urls = this.configurationService.getUrlsObject();
  }

  getProjectList(limit: number, offset = 0): Observable<IProject[]> {
    return this.dataService.get<IProject[]>(
      this.configurationService.getUrl(
        this.urls.PROJECT_MANAGEMENT.BASE,
        this.urls.PROJECT_MANAGEMENT.GET_PROJECT_LIST,
        limit
      ) + '?offset=' + offset);
  }

  getProject(projectId: number): Observable<IProject> {
    return this.dataService
      .get<IProject>(this.configurationService.getUrl(this.urls.PROJECT_MANAGEMENT.GET_PROJECT, projectId))
      .pipe(
        map((project: IProject) => {
          this.currentProject$.next(project);
          return project;
        })
      );
  }

  getProjectsCount(): Observable<number> {
    return this.dataService.get<number>(
      this.configurationService.getUrl(
        this.urls.PROJECT_MANAGEMENT.BASE + '/count'
      ));
  }

  getProductSelectors() {
    const url = this.configurationService.getProductSelectorLink();

    return this.dataService.getWithCache(url);
  }

  getDevicesCountForEnclosure(projectId: number, enclosureId: number) {
    const url = this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure/' + enclosureId + '/device/count');

    return of(true).pipe(
      delay(500),
      switchMap(() => {
        return this.dataService.get(url).pipe(
          map(result => {
            if (result?.deviceCount >= 0) {
              this.setEnclosureDeviceCount(result?.deviceCount);
            }
          })
        );
      })
    )
  }

  checkIfProjectNameExists(projectName: string): Observable<boolean> {
    return this.dataService
      .putWithId<boolean>(this.configurationService.getUrl(
        this.urls.PROJECT_MANAGEMENT.BASE,
        this.urls.PROJECT_MANAGEMENT.NAME_CHECK,
      ), {projectName});
  }

  getCurrentProject$() {
    return this.currentProject$.asObservable().pipe(
      filter(e => !!e)
    );
  }

  setCurrentProjectNull$() {
    this.currentEnclosure$.next(null);
    this.currentProject$.next(null);
  }

  getCurrentEnclosure$() {
    return this.currentEnclosure$.asObservable().pipe(
      filter(e => !!e),
      distinctUntilChanged()
    );
  }

  getCurrentEnclosureDeviceCount$() {
    return this.currentEnclosureDeviceCount$.asObservable();
  }

  increaseEnclosureDeviceCount(value?: number) {
    this.currentEnclosureDeviceCount$.next(
      this.currentEnclosureDeviceCount$.getValue() + (value === undefined ? 1 : value)
    );
  }

  decreaseEnclosureDeviceCount(value?: number) {
    this.currentEnclosureDeviceCount$.next(
      this.currentEnclosureDeviceCount$.getValue() - (value === undefined ? 1 : value)
    );
  }

  private setCurrentEnclosure(enclosure: IEnclosure) {
    this.currentEnclosure$.next(enclosure);
    this.currentEnclosureDeviceCount$.next(
      filterOutDevicesThatShouldNotBePlacedInBOM(enclosure?.devices)?.length || 0
    );
  }

  setEnclosureDeviceCount(deviceCount: number) {
    this.currentEnclosureDeviceCount$.next(deviceCount);
  }

  setCurrentEnclosureBreadcrumb(enclosure: IEnclosure) {
    this.setCurrentEnclosure(enclosure);
  }

  updateProject(project: IProject): Promise<IProject> {
    const projectSaveUrl = this.configurationService.getUrl(this.urls.PROJECT_MANAGEMENT.BASE);

    return this.dataService.putWithId<IProject>(projectSaveUrl, project).pipe(
      map((p: IProject) => {
        this.currentProject$.next(p);
        return p;
      })
    ).toPromise();
  }

  resetProjectInstallationDate(projectId: number): Promise<IProject> {
    const url = this.configurationService.getUrl('project', projectId, 'resetInstallationDate');

    return this.dataService.putWithId<IProject>(url, {}).toPromise();
  }

  createNewProject(project: IProject): Promise<IProject> {
    const projectSaveUrl = this.configurationService.getUrl(this.urls.PROJECT_MANAGEMENT.BASE);

    return this.dataService.post<IProject>(projectSaveUrl, project).toPromise();
  }

  deleteProject(project: IProject) {

    return this.dataService.delete(
      this.configurationService.getUrl(this.urls.PROJECT_MANAGEMENT.BASE, project.projectId)
    );
  }

  copyProject(projectName: string, projectId: number) {
    const url = this.configurationService.getUrl( 'project', projectId, 'copyProject');

    return this.dataService.post<IProject>(url, {projectName});
  }

  copyCompanyProject(projectName: string, projectId: number) {
    const url = this.configurationService.getUrl( 'CompanyAdmin/project', projectId, 'copyProject');

    return this.dataService.post<IProject>(url, {projectName});
  }

  getProjectEnclosures(projectId: number, hideDevices = false): Observable<IEnclosure[]> {
    return this.dataService
      .get<IEnclosure[]>(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure?hideDevices=' + hideDevices));
  }

  getProjectEnclosuresInfo(projectId: number): Observable<IEnclosureInfo[]> {
    return this.dataService
      .get<IEnclosure[]>(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosures-info'));
  }

  getProjectOverviewEnclosures(projectId: number): Observable<IEnclosureProjectOverview[]> {
    return this.dataService
      .get<IEnclosureProjectOverview[]>(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure-project-overview'));
  }

  updateEnclosureName(name: string, projectId: number, enclosureId: number, enclosure: IEnclosure) {
    return this.dataService.post(this.configurationService.getUrl(
      'PCD', 'project', projectId, 'enclosure', enclosureId, 'name'), {name})
      .pipe(
        map((updatedEnclosure: IEnclosure) => {
          enclosure.name = updatedEnclosure.name;
          this.updateCurrentEnclosure({...enclosure});
          return enclosure;
        })
      );
  }

  updateEnclosureProperty(projectId: number, enclosureId: number, enclosure: IEnclosure, enclosureProperty: IEnclosureProperties) {
    return this.dataService.putWithId(this.configurationService.getUrl('PCD', 'project', projectId,
      'enclosure', enclosureId, 'update_enclosure_property'), enclosureProperty)
      .pipe(
        map((updatedEnclosureProperty: IEnclosureProperties) => {
          this.updateCurrentEnclosure({ ...updateEnclosureProperty(enclosure, updatedEnclosureProperty) });
          return enclosure;
        })
      )
  }

  updateEnclosure(enclosure: ISaveEnclosure) {
    const url = this.configurationService.getUrl('PCD', 'project', enclosure.projectId, 'enclosure', enclosure.enclosureId);
    return this.dataService.putWithId(url, enclosure)
      .pipe(
        map((updatedEnclosure: IEnclosure) => this.updateCurrentEnclosure(updatedEnclosure))
      );
  }

  updateEnclosurePragmaMulti(enclosure: IPragmaDTO) {
    const url = this.configurationService.getUrl('PCD', 'project', enclosure.projectId, 'enclosure', enclosure.enclosureId, 'pragma');
    return this.dataService.putWithId(url, enclosure)
      .pipe(
        map((updatedEnclosure: IEnclosure) => this.updateCurrentEnclosure(updatedEnclosure))
      );
  }

  updateEnclosurePrismaXS(enclosure: IPrismaXSDTO, skipUpdate = false) {
    const url = this.configurationService.getUrl('PCD', 'project', enclosure.projectId, 'enclosure', enclosure.enclosureId, 'prismaxs');
    return this.dataService.putWithId(url, enclosure)
      .pipe(
        map((updatedEnclosure: IEnclosure) => {
          if (!skipUpdate) {
            this.updateCurrentEnclosure(updatedEnclosure);
          }

          return updatedEnclosure;
        })
      );
  }

  getProjectEnclosure(projectId: number, enclosureId: number): Observable<IEnclosure> {

    return this.dataService
      .get(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure/' + enclosureId))
      .pipe(
        map((enclosure: IEnclosure) => this.updateCurrentEnclosure(enclosure))
      );
  }

  private updateCurrentEnclosure(enclosure: IEnclosure) {
    this.setCurrentEnclosure(enclosure);
    return enclosure;
  }

  getEnclosurePlacedDevices(projectId: number, enclosureId: number): Observable<IDevicesOnGrid[]> {

    return this.dataService
      .get(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure/' + enclosureId + '/device'));
  }

  getEnclosurePlacedDevicesInSldMode(projectId: number, enclosureId: number): Observable<IDevicesOnGrid[]> {
    return this.dataService
      .get(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure/' + enclosureId + '/device'
        + '/sld/true'));
  }

  addDevice(projectID: number, enclosureID: number,
            device: IProduct, placePosition: IPosition,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
            deviceWidth: number, onSwitchboard: boolean, position: IGridPositionIndex, options: string[]): Observable<any> {

    const apiEndpoint = this.getAddDeviceEndpoint(device, projectID, enclosureID);

    const deviceData = {
      ...device,
      projectId: projectID,
      productId: device.productId,
      rowNumber: placePosition.top,
      columnNumber: placePosition.left,
      enclosureField: position.left,
      enclosureRow: position.top,
      label: device.productRange.label,
      width: deviceWidth,
      onSwb: onSwitchboard ? 1 : 0,
      enumberInput: device?.productCharacteristicsRules?.isUniversalDevice ? device.enumber.toString() : null,
      options
    };

    return this.dataService.post(apiEndpoint, deviceData);
  }

  addDeviceWithoutCheckingForFeedingTreeConflict(projectID: number, enclosureID: number,
            device: IProduct, placePosition: IPosition,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
            deviceWidth: number, onSwitchboard: boolean, position: IGridPositionIndex, options: string[]): Observable<any> {

    const apiEndpoint = this.getAddDeviceEndpoint(device, projectID, enclosureID) + '/true';

    const deviceData = {
      ...device,
      projectId: projectID,
      productId: device.productId,
      rowNumber: placePosition.top,
      columnNumber: placePosition.left,
      enclosureField: position.left,
      enclosureRow: position.top,
      label: device.productRange.label,
      width: deviceWidth,
      onSwb: onSwitchboard ? 1 : 0,
      enumberInput: device?.productCharacteristicsRules?.isUniversalDevice ? device.enumber.toString() : null,
      options
    };

    return this.dataService.post(apiEndpoint, deviceData);
  }

  private getAddDeviceEndpoint(device: IProduct, projectID: number, enclosureID: number) {
    if(device?.productCharacteristicsRules?.isUniversalDevice) {
      return this.configurationService.getUrl('PCD/project/' + projectID + '/enclosure/' + enclosureID + '/' + 'custom_device');
    }
    if(device?.productCharacteristicsRules?.isElectricalClamp) {
      return this.configurationService.getUrl('CustomDevice/project/' + projectID + '/enclosure/' + enclosureID + '/' + 'electrical_clamp');
    }

    return this.configurationService.getUrl('PCD/project/' + projectID + '/enclosure/' + enclosureID + '/' + 'device');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addDevices(projectID: number, enclosureID: number, updateDevices: IPlacedDevice[]): Observable<any> {
    const devicesData = updateDevices.map(d => {
      return {
          ...d.device,
          projectId: projectID,
          productId: d.device.productId,
          rowNumber: d.position.top,
          columnNumber: d.position.left,
          enclosureField: d.enclosureField.left,
          enclosureRow: d.enclosureField.top,
          label: d.device.productRange.label,
          width: d.width,
          onSwb: 1,
          enumberInput: d.device?.isCustomDevice ? d.device.enumber.toString() : null,
          options: d.options
        }
      });

    return this.dataService
      .post(this.configurationService.getUrl('PCD/project/' + projectID + '/enclosure/' + enclosureID + '/devices'),
        devicesData);
  }

  configureWizard(projectID: number,
                  enclosureID: number,
                  configurationDevices: IWizardConfigurationDevicesDto,
                  enclosure: ISaveEnclosure,
                  breakingCapacity: BreakingCapacityType): Observable<IEnclosure> {
    return this.dataService
      .post(this.configurationService.getUrl('PCD/project/' + projectID + '/enclosure/' + enclosureID + '/wizard_devices'),
        { devices: configurationDevices?.devices, customDevices: configurationDevices?.customDevices, enclosure, breakingCapacity })
      .pipe(
        map((updatedEnclosure: IEnclosure) => this.updateCurrentEnclosure(updatedEnclosure))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addDevicesWithoutCheckingSldTreeConflict(projectID: number, enclosureID: number, updateDevices: IPlacedDevice[]): Observable<any> {
    const devicesData = updateDevices.map(d => {
      const isUniversalProduct = d.device?.isCustomDevice;
      return {
        ...d.device,
        projectId: projectID,
        productId: d.device.productId,
        rowNumber: d.position.top,
        columnNumber: d.position.left,
        enclosureField: d.enclosureField.left,
        enclosureRow: d.enclosureField.top,
        label: d.device.productRange.label,
        width: d.width,
        onSwb: 1,
        enumberInput: isUniversalProduct ? d.device.enumber.toString() : null,
        options: d.options
      }
    });

    return this.dataService
      .post(this.configurationService.getUrl('PCD/project/' + projectID + '/enclosure/' + enclosureID + '/devices/true'),
        devicesData);
  }

  updateDevice(projectID: number, enclosureID: number,
               device: IPlacedDevice, position: IGridPositionIndex): Observable<IDevice | IUpdateDeviceWithConflict> {
    return this.dataService
      .putWithId(this.configurationService.getUrl(
        'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/device/' + device.id),
        updateDeviceFactory(projectID, device, position));
  }

  updateDeviceWithoutCheckingForSldTConflict(projectID: number,
                                             enclosureID: number,
                                             device: IPlacedDevice,
                                             position: IGridPositionIndex): Observable<IDevice> {
    return this.dataService
      .putWithId(this.configurationService.getUrl(
          'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/device/' + device.id + '/true'),
        updateDeviceFactory(projectID, device, position));
  }

  updateDevicesWithoutCheckingForSldTConflict(projectID: number,
                                              enclosureID: number,
                                              devices: IPlacedDevice[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                              position: IGridPositionIndex): Observable<any> {
    return this.dataService
      .putWithId(this.configurationService.getUrl(
          'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/multiple_devices/true'),
        updateMultipleDevicesFactory(projectID, devices, position));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateDevices(projectID: number, enclosureID: number, devices: IPlacedDevice[], position: IGridPositionIndex): Observable<any> {
    return this.dataService
      .putWithId(this.configurationService.getUrl(
        'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/multiple_devices'),
        updateMultipleDevicesFactory(projectID, devices, position));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateDeviceOptions(projectID: number, enclosureID: number, deviceId: number, options: string[]): Observable<any> {
    return this.dataService
      .putWithId(this.configurationService.getUrl(
        'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/device/' + deviceId + '/options'),
        {
          options
        });
  }

  deleteDevice(projectID: number, enclosureID: number,
               placedDeviceID: number): Observable<IDevice | IUpdateDeviceWithConflict> {
    return this.dataService
      .delete(this.configurationService.getUrl(
        // eslint-disable-next-line max-len
        'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/device/' + placedDeviceID)) as Observable<IDevice | IUpdateDeviceWithConflict>;
  }

  deleteDeviceWithoutCheckingForFeedingTreeConflict(projectID: number, enclosureID: number,
               placedDeviceID: number): Observable<IDevice> {
    return this.dataService
      .delete(this.configurationService.getUrl(
        'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/device/' + placedDeviceID + '/true')) as Observable<IDevice>;
  }

  deleteMultipleDevices(projectID: number, enclosureID: number, devicesIds: number[]): Observable<number[] | IUpdateDeviceWithConflict> {
    return this.dataService
      .putWithId(this.configurationService.getUrl(
        'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/delete_multiple'),
        {
          devicesIds
        });
  }

  deleteMultipleDevicesWithoutCheckingForFeedingTreeConflict(projectID: number,
                                                             enclosureID: number,
                                                             devicesIds: number[]): Observable<number[]> {
    return this.dataService
      .putWithId(this.configurationService.getUrl(
          'PCD/project/' + projectID + '/enclosure/' + enclosureID + '/delete_multiple/true'),
        {
          devicesIds
        });
  }

  generateInvitationLinkForProject(projectId: number, isIncomingUserEditor: boolean) {
    return this.dataService
      .post(this.configurationService.getUrl('InviteUser/project/' + projectId + '/link'), {
        ProjectId: Number(projectId),
        IsIncomingUserEditor: isIncomingUserEditor
      });
  }

  generateLinkTenderingModeProject(projectId: number): Observable<IProject> {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.putWithId<IProject>(baseUrl + `/api/v1/project/${projectId}/link_tendering_mode`, null);
  }

  removeUserFromProject(projectId: number, ownerId: number) {
    return this.dataService
      .putWithId(this.configurationService.getUrl('InviteUser/project/' + projectId + '/removeuser'), {
        UserWhoInvitedId: ownerId,
        ProjectId: Number(projectId)
      });
  }

  switchRoleForUser(projectId: number, userId: number, canEditProject: boolean) {
    return this.dataService
      .putWithId(this.configurationService.getUrl('project/' + projectId + '/switchrole'), {
        canEditProject,
        userId
      });
  }

  acceptProject(token: string) {
    return this.dataService
      .get(this.configurationService.getUrl('InviteUser/accept?token=' + encodeURI(token)));
  }

  claimProject(token: string) {
    return this.dataService
      .get(this.configurationService.getUrl('InviteUser/claim?token=' + encodeURI(token)));
  }

  declineProject(token: string, projectId: number) {
    return this.dataService
      .delete(this.configurationService.getUrl('InviteUser/decline/' + projectId + '?token=' + encodeURI(token)));
  }

  sharedProjects(limit: number, offset = 0) {
    return this.dataService
      .get(this.configurationService.getUrl('InviteUser/shared/' + limit + '?offset=' + offset));
  }

  projectsStats() {
    return this.dataService
      .get(this.configurationService.getUrl('InviteUser/project_stats'));
  }

  pendingProjects() {
    return this.dataService
      .get(this.configurationService.getUrl('InviteUser/pending'));
  }

  companyProjects() {
    return this.dataService
      .get(this.configurationService.getUrl('CompanyAdmin/company_projects'));
  }

  companyArchivedProjects() {
    return this.dataService
      .get(this.configurationService.getUrl('CompanyAdmin/archived_company_projects'));
  }

  createNewProjectEnclosure(projectId: number): Observable<IEnclosure> {

    return this.dataService
      .post(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure'), {});
  }

  deleteProjectEnclosure(projectId: number, enclosureId: number): Observable<IEnclosure> {

    return this.dataService
      .delete(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure/' + enclosureId), {})
      .pipe(
        map((enclosure: IEnclosure) => {
          return enclosure;
        })
      );
  }

  copyProjectEnclosure(projectId: number, enclosureId: number, enclosureDetails: ICopyEnclosure): Observable<IEnclosure> {

    return this.dataService
      .post(this.configurationService.getUrl('PCD/project/' + projectId + '/enclosure/' + enclosureId + '/copy'), enclosureDetails);
  }

  postFile(fileToUpload: File, projectId: number): Observable<IAssociateFileUpload[]> {
    const baseUrl = environment.apiEndpoint;
    const url: string = baseUrl + '/api/v1/File/project/' + projectId + '/UploadFile';
    const formData: FormData = new FormData();
    formData.append('Image', fileToUpload, fileToUpload.name);
    formData.append('projectId', projectId.toString());

    return this.httpClient.post<IAssociateFileUpload[]>(url, formData, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('token'))
      })
    });
  }

  deleteFile(projectId: number, fileId: number, savedFileName: string): Observable<IAssociateFileUpload[]> {
    const baseUrl = environment.apiEndpoint;
    // eslint-disable-next-line max-len
    return this.dataService.delete(baseUrl + `/api/v1/file/project/${projectId}/DeleteFile/${fileId}/${savedFileName}`) as Observable<IAssociateFileUpload[]>;
  }

  public downloadAssociatedFile(projectId: number, fileId: number, savedFileName: string, mimetype: string, fileName: string) {
    const baseUrl = environment.apiEndpoint;
    const data: IAssociateFileUpload = {} as IAssociateFileUpload;
    data.fileId = fileId;
    data.fileName = savedFileName;
    data.projectId = projectId;
    data.mimeType = mimetype;

    const url: string = baseUrl + `/api/v1/file/project/${projectId}/DownloadFile`;
    const res = this.httpClient.post(url, data, {
      responseType: 'blob',
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('token'))
      })
    });

    return res.toPromise().then(d => {
      this.bomService.downloadFile(d, mimetype, fileName);
    });
  }

  getFavoriteProducts(projectId: number, enclosureId: number) {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.get(baseUrl + `/api/v1/FavoriteProduct/project/${projectId}/enclosure/${enclosureId}/favorite`);
  }

  removeMyselfFromSharedProject(projectId: number) {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.delete(baseUrl + `/api/v1/InviteUser/project/${projectId}/removeMyself`);
  }

  transferOwnership(projectId: number, memberId: number) {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.putWithId(baseUrl + `/api/v1/project/${projectId}/transferownership`,  {
      sharedUserId: memberId
    });
  }

  setCurrentProjectEditableStatus$(project: IProject) {
    const isEditable = new CanEditProjectPipe().transform(project);
    this.currentProjectEditable$.next(isEditable);
  }

  getIsCurrentProjectEditable$() {
    return this.currentProjectEditable$.asObservable();
  }

  getAllCompanyMembers() {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.get(baseUrl + '/api/v1/CompanyAdmin/company/members');
  }

  updateProjectsWithNewOwner(projectIds: number[], newOwnerId: number) {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.putWithId(baseUrl + '/api/v1/CompanyAdmin/project/assign',  {
      newOwnerId,
      projectIds
    });
  }

  getCompanyAdminsOfProjects(): Observable<ICompanyAdminsOfProjects> {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.get(baseUrl + '/api/v1/CompanyAdmin/company-admins-of-projects');
  }

  getArchivedProjectList(limit: number, offset = 0): Observable<IProject[]> {
    const baseUrl = environment.apiEndpoint;
    return this.dataService.get(baseUrl + '/api/v1/project/archived/' + limit + '?offset=' + offset);
  }

  getArchivedProjectsCount(): Observable<number> {
    return this.dataService.get<number>(
      this.configurationService.getUrl(
        this.urls.PROJECT_MANAGEMENT.BASE + '/archived-count'
      ));
  }

  archiveProject(project: IProject): Observable<number> {
    const baseUrl = environment.apiEndpoint;

    return this.dataService.putWithId(baseUrl + `/api/v1/project/${project.projectId}/archive`,  {});
  }

  restoreArchivedProject(project: IProject): Observable<number> {
    const baseUrl = environment.apiEndpoint;

    return this.dataService.putWithId(baseUrl + `/api/v1/project/${project.projectId}/restore-archived`,  {});
  }

  getCurrentProjectId(): Observable<number> {
    return this.getCurrentProject$()
      .pipe(
        take(1),
        filter(project => !!project),
        map(project => project.projectId)
      )
  }

  getOccupancyStatus(projectId: number): Observable<IEnclosureOccupancy[]> {
    const url = this.configurationService.getUrl(`PCD/occupancy/project/${projectId}`);
    return this.dataService.get<IEnclosureOccupancy[]>(url);
  }

}
